import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  SimpleGrid,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import PoolCardScroller from "../components/PoolCardScroller";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getActiveSeason, getMyMlbPools, getAllMlbPools, createMlbPool, joinMlbPool } from "../api/mlbHrDerby";
import mlbLogo from "../assets/logos/mlb.png";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

export default function MlbHrDerbyHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [myPools, setMyPools]         = useState([]);
  const [allPools, setAllPools]       = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolError, setPoolError]     = useState("");

  const [newPoolName,    setNewPoolName]    = useState("");
  const [newPoolDepth,   setNewPoolDepth]   = useState("");
  const [creating,       setCreating]       = useState(false);
  const [createError,    setCreateError]    = useState("");
  const [joiningPoolId,  setJoiningPoolId]  = useState(null);
  const [joinError,      setJoinError]      = useState("");
  const [season,         setSeason]         = useState(null);

  useEffect(() => {
    loadPools();
  }, []);

  async function loadPools() {
    setLoadingPools(true);
    setPoolError("");
    try {
      const s = await getActiveSeason();
      setSeason(s);
      const [mine, all] = await Promise.all([
        getMyMlbPools(s.id),
        getAllMlbPools(s.id),
      ]);
      setMyPools(mine);
      setAllPools(all);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to load pools.");
      setMyPools([]);
      setAllPools([]);
    } finally {
      setLoadingPools(false);
    }
  }

  async function handleCreatePool() {
    setCreateError("");
    if (!newPoolName.trim()) { setCreateError("Pool name is required."); return; }
    setCreating(true);
    try {
      const pool = await createMlbPool(season.id, {
        name: newPoolName.trim(),
        pool_depth: newPoolDepth ? parseInt(newPoolDepth, 10) : null,
      });
      setNewPoolName("");
      setNewPoolDepth("");
      navigate(`/mlb-hr-derby/current/${pool.id}/draft`);
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed to create pool.");
    } finally {
      setCreating(false);
    }
  }

  async function handleJoinPool(poolId) {
    setJoinError("");
    setJoiningPoolId(poolId);
    try {
      await joinMlbPool(season.id, poolId);
      navigate(`/mlb-hr-derby/current/${poolId}/draft`);
    } catch (err) {
      setJoinError(err?.response?.data?.detail || "Failed to join pool.");
      setJoiningPoolId(null);
    }
  }

  const joinablePools = useMemo(() => {
    const myPoolIds = new Set(myPools.map((p) => p.id));
    return allPools.filter((p) => !myPoolIds.has(p.id));
  }, [myPools, allPools]);

  const seasonStarted = !!season?.season_started;
  const creationOpen = !!season?.pool_creation_open;
  // Rosters lock on opening day, which is the same moment the season starts.
  const rosterLocked = seasonStarted;

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function handleOpenPool(poolId) {
    navigate(`/mlb-hr-derby/current/${poolId}`);
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="HR DERBY LOBBY"
        onBack={() => navigate("/")}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="stretch" spacing={8}>
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" color="whiteAlpha.900">
              Welcome to the HR Derby lobby. Enter your current game, view open pools, or start a new pool.
            </Text>
            {creationOpen && season && (
              <Alert status="success" borderRadius="md" mt={1} color="green.900">
                <AlertIcon flexShrink={0} />
                <Text ml={2}>
                  The <strong>{season.name}</strong> season is available for set up — create your pool below!
                </Text>
              </Alert>
            )}
          </VStack>

          {poolError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {poolError}
            </Alert>
          )}

          {/* My Pools + Join row */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Your Active Games */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              position="relative"
              overflow="hidden"
            
              opacity={!loadingPools && myPools.length === 0 ? 0.5 : 1}
            >
              {/* Watermark */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={mlbLogo} alt="" style={{ width: "500px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Your Active Games</Heading>
                <Text color="gray.700">Pools you belong to for the current MLB season.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : myPools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      You are not currently in any pools.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {myPools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <HStack spacing={3} flexShrink={0}>
                            {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                              <Button variant="link" size="sm" color={TOP_BAR_BLUE} onClick={() => navigate(`/mlb-hr-derby/setup/${pool.id}`)}>Edit</Button>
                            )}
                            <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" onClick={() => handleOpenPool(pool.id)}>Open</Button>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: card grid */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {myPools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">Participants: {pool.user_count}</Text>
                            {pool.total_pot != null && (
                              <Text fontSize="sm" color="gray.600" textAlign="center">Total Pot: ${pool.total_pot}</Text>
                            )}
                            <HStack spacing={2} justify="center">
                              {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                                <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} onClick={() => navigate(`/mlb-hr-derby/setup/${pool.id}`)} _hover={{ bg: "gray.100" }}>Edit Pool</Button>
                              )}
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white" onClick={() => handleOpenPool(pool.id)} _hover={{ bg: "#00004a" }}>Open Pool</Button>
                            </HStack>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Join an Open Pool */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              position="relative"
              overflow="hidden"
              opacity={creationOpen ? 1 : 0.45}
              pointerEvents={creationOpen ? "auto" : "none"}
            >
              {/* Watermark */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={mlbLogo} alt="" style={{ width: "500px" }} />
              </Box>

              <VStack align="stretch" spacing={5} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Join an Open Pool</Heading>
                {rosterLocked ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    Joining a pool is not available once the season has started
                  </Alert>
                ) : (
                  <Text color="gray.700">Available pools for the current season.</Text>
                )}
                {joinError && (
                  <Alert status="error" borderRadius="md"><AlertIcon />{joinError}</Alert>
                )}
                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : joinablePools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      No open pools available right now.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {joinablePools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" isLoading={joiningPoolId === pool.id} onClick={() => handleJoinPool(pool.id)}>Join</Button>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: card grid */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {joinablePools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">
                              {pool.user_count} participant{pool.user_count !== 1 ? "s" : ""}
                              {pool.total_pot != null ? ` · $${pool.total_pot} pot` : ""}
                            </Text>
                            <Button size="sm" bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }} isLoading={joiningPoolId === pool.id} onClick={() => handleJoinPool(pool.id)}>Join</Button>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Rules + New Pool */}
          <Box
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={8}
          >
            <VStack align="stretch" spacing={8}>

              {/* 1 — Want to Start a New Pool? */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>
                  Want to Start a New Pool?
                </Heading>
                {rosterLocked && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    New games can't be created mid season
                  </Alert>
                )}
              </Box>

              {/* 2 — Rules */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Rules</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    Pick <strong>six players</strong> you believe will hit the most home runs during the regular season.
                  </ListItem>
                  <ListItem>
                    Your six players&apos; combined previous-season HR total must be{" "}
                    <strong>130 or fewer</strong>.
                  </ListItem>
                  <ListItem>
                    You are allowed <strong>one roster change</strong> at any point during the season.
                  </ListItem>
                  <ListItem>
                    The replacement player must not exceed the removed player&apos;s HR total in{" "}
                    <strong>(a) the previous season</strong> or{" "}
                    <strong>(b) the current season at the time of the swap</strong>.
                  </ListItem>
                  <ListItem>
                    You may submit <strong>more than one entry</strong> per pool, each with a unique team name.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* 3 — Scoring */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Scoring</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    Your score is the <strong>total home runs</strong> hit by your six active players during the regular season.
                  </ListItem>
                  <ListItem>
                    The leaderboard updates each morning as stats are pulled from MLB.
                  </ListItem>
                  <ListItem>
                    In the event of a tie, the pot is split.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* 4 — Pool Name / Depth / Button */}
              <Box>
                <VStack align="stretch" spacing={4}>
                  {createError && (
                    <Alert status="error" borderRadius="md"><AlertIcon />{createError}</Alert>
                  )}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired isDisabled={!creationOpen}>
                      <FormLabel>Pool Name</FormLabel>
                      <Input
                        value={newPoolName}
                        onChange={(e) => { setNewPoolName(e.target.value); setCreateError(""); }}
                        placeholder="Enter pool name"
                      />
                    </FormControl>
                    <FormControl isDisabled={!creationOpen}>
                      <FormLabel>Pool Depth ($)</FormLabel>
                      <Input
                        type="number"
                        min={1}
                        value={newPoolDepth}
                        onChange={(e) => setNewPoolDepth(e.target.value)}
                        placeholder="Optional buy-in amount"
                      />
                    </FormControl>
                  </SimpleGrid>
                  <HStack spacing={4} flexWrap="wrap">
                    <Button
                      bg={TOP_BAR_BLUE}
                      color="white"
                      _hover={{ bg: "#00004a" }}
                      isLoading={creating}
                      loadingText="Creating..."
                      isDisabled={!creationOpen}
                      onClick={handleCreatePool}
                    >
                      Start New Pool
                    </Button>
                  </HStack>
                  {!creationOpen && !seasonStarted && (
                    <Text mt={4} color="gray.700">
                      Once next season setup is available, this page will allow users to create a
                      new pool before the season begins.
                    </Text>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
